/**
 * Cliente realtime (SSE) — DESIGN-V8 Etapa 2.
 *
 * EventSource não manda Authorization, então o stream é consumido com fetch +
 * leitura do body (parser SSE próprio): frames separados por linha em branco,
 * linhas `data:` viram JSON validado pelo RealtimeEventSchema, comentários
 * (`: ping`) são ignorados mas contam como atividade para o watchdog.
 *
 * O token vem de getToken() — a MESMA fonte do api-client (localStorage no
 * modo local; provider do keycloak-js no modo keycloak, que renova o token em
 * memória). Ele é lido a cada (re)conexão, então reconectar já usa o token
 * renovado.
 *
 * Reconexão com backoff exponencial capado (1s, 2s, 5s, 10s, 30s), zerado
 * quando um stream abre com sucesso. Watchdog de inatividade: o servidor manda
 * `: ping` a cada 25s — 60s sem bytes ⇒ conexão morta ⇒ aborta e reconecta.
 *
 * Singleton por page load, iniciado pelo AppLayout (rotas autenticadas do
 * staff — nunca no widget público nem no portal). start é refcontado e
 * seguro sob StrictMode (montagem dupla).
 */
import { useSyncExternalStore } from 'react';
import { RealtimeEventSchema, type RealtimeEvent } from '@erp/shared';
import { env } from '../config/env';
import { authHeaders, getToken } from './api-client';

const BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 30_000] as const;
const IDLE_TIMEOUT_MS = 60_000;

// ── Estado reativo `connected` ──────────────────────────────────────────────

let connected = false;
let everConnected = false;
const connectedListeners = new Set<() => void>();

function setConnected(value: boolean) {
  if (connected === value) return;
  connected = value;
  if (value) everConnected = true;
  for (const l of connectedListeners) l();
}

/** Getter módulo-level: sempre fresco, para uso fora do render (queryFn). */
export function isRealtimeConnected(): boolean {
  return connected;
}

/** True se o stream já abriu ao menos uma vez neste page load. */
export function hasRealtimeEverConnected(): boolean {
  return everConnected;
}

/** Estado reativo da conexão SSE (re-renderiza quando conecta/cai). */
export function useRealtimeConnected(): boolean {
  return useSyncExternalStore(
    (cb) => {
      connectedListeners.add(cb);
      return () => connectedListeners.delete(cb);
    },
    () => connected
  );
}

// ── Assinantes de eventos ───────────────────────────────────────────────────

const handlers = new Set<(e: RealtimeEvent) => void>();

/** Registra um handler de eventos; retorna o unsubscribe. */
export function subscribeRealtime(handler: (e: RealtimeEvent) => void): () => void {
  handlers.add(handler);
  return () => {
    handlers.delete(handler);
  };
}

function dispatch(event: RealtimeEvent) {
  for (const h of handlers) h(event);
}

// ── Loop de conexão ─────────────────────────────────────────────────────────

let consumers = 0;
let generation = 0;
let controller: AbortController | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let wakeRetry: (() => void) | null = null;

/**
 * Liga o stream (refcontado): o primeiro consumidor inicia o loop, o último a
 * sair aborta a conexão e cancela qualquer retry pendente. Retorna o stop.
 */
export function startRealtime(): () => void {
  consumers++;
  if (consumers === 1) {
    generation++;
    void runLoop(generation);
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    consumers--;
    if (consumers > 0) return;
    generation++; // invalida o loop atual
    controller?.abort();
    controller = null;
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    wakeRetry?.(); // acorda o sleep para o loop ver a geração vencida e sair
    setConnected(false);
  };
}

async function runLoop(myGeneration: number): Promise<void> {
  let attempt = 0;
  while (generation === myGeneration) {
    const token = getToken();
    if (token) {
      // Controller LOCAL do loop, publicado no módulo sem nenhum await entre o
      // check de geração (topo do while) e a publicação: um stop() posterior
      // sempre vê — e aborta — exatamente este controller. Depois de QUALQUER
      // await, o estado compartilhado (controller/connected) só pode ser
      // tocado se a geração ainda for a nossa; um loop vencido retorna sem
      // mexer em nada, senão ele clobberaria o controller do loop novo e o
      // logout/stop não conseguiria mais abortar o stream vivo.
      const ctrl = new AbortController();
      controller = ctrl;
      try {
        const url = new URL(`${env.apiBaseUrl}/api/v1/events/stream`, window.location.origin);
        const res = await fetch(url, {
          // o token do loop (não o getToken do momento) + X-Tenant da empresa escolhida
          headers: { ...authHeaders(), Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
          cache: 'no-store',
          signal: ctrl.signal,
        });
        if (generation !== myGeneration) {
          // stop() correu durante o fetch mas a promise resolveu mesmo assim:
          // só fecha o body e sai — sem setConnected, sem tocar `controller`.
          await res.body?.cancel().catch(() => undefined);
        } else if (res.ok && res.body) {
          attempt = 0; // stream aberto com sucesso ⇒ backoff zera
          setConnected(true);
          await readStream(res.body, ctrl);
        } else {
          await res.body?.cancel().catch(() => undefined);
        }
      } catch {
        // rede caiu / abort / timeout do watchdog — o backoff decide o resto
      }
      if (generation !== myGeneration) return; // stop() já limpou controller/connected
      setConnected(false);
      controller = null;
    }
    if (generation !== myGeneration) return;
    const delay = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)] ?? 30_000;
    attempt++;
    await sleep(delay);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    wakeRetry = () => {
      wakeRetry = null;
      resolve();
    };
    retryTimer = setTimeout(() => {
      retryTimer = null;
      wakeRetry = null;
      resolve();
    }, ms);
  });
}

// ── Parser SSE ──────────────────────────────────────────────────────────────

async function readStream(body: ReadableStream<Uint8Array>, ctrl: AbortController): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  // Heartbeat do servidor a cada 25s: 60s sem NENHUM byte = conexão morta.
  const armIdle = () => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => ctrl.abort(), IDLE_TIMEOUT_MS);
  };
  armIdle();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) return;
      armIdle();
      // Normaliza CRLF na concatenação (cobre \r\n partido entre chunks).
      buffer = (buffer + decoder.decode(value, { stream: true })).replace(/\r\n/g, '\n');
      let idx: number;
      while ((idx = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        handleFrame(frame);
      }
    }
  } finally {
    if (idleTimer) clearTimeout(idleTimer);
    reader.releaseLock();
  }
}

function handleFrame(frame: string) {
  // Só o campo `data:` interessa; comentários (`: ping`) e outros campos
  // (event/id/retry — o servidor não usa) são ignorados.
  const data = frame
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).replace(/^ /, ''))
    .join('\n');
  if (!data) return;
  let json: unknown;
  try {
    json = JSON.parse(data);
  } catch {
    return; // payload malformado: ignora, o polling de fallback cobre
  }
  const parsed = RealtimeEventSchema.safeParse(json);
  if (!parsed.success) return; // evento de versão mais nova do servidor: ignora
  dispatch(parsed.data);
}
